import React, { useEffect, useMemo, useState } from 'react';
import { Search, X, MapPin } from 'lucide-react';
import { api } from '../../lib/api';
import AdminOrgMap from './AdminOrgMap';
import PageLoader from '../shared/PageLoader';

function typeColor(type) {
  if (type === 'medical_practice') return '#0DA7CA';
  if (type === 'law_firm')         return '#8b5cf6';
    if (type === 'marketer')         return '#a78bfa';
  return '#6b7280';
}

// Full-page version of the dashboard's geographic view — the whole viewport
// is map, with just a type filter and a pin count up top.
export default function AdminMapPage() {
  const [orgs, setOrgs] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [typeFilter, setTypeFilter] = useState(new Set(['medical_practice', 'law_firm']));
  const [search, setSearch] = useState('');
  const [focus, setFocus] = useState(null);   // {lat, lng} to fly to

  useEffect(() => {
    api.getOrgsOverview()
      .then(res => {
        setOrgs(Array.isArray(res) ? res : (res?.orgs || []));
        setLocations(res?.locations || []);
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  // Search matches organizations AND their locations by name, city, state
  // or address — a facility should be findable by its own name, not only
  // its parent's.
  const locsByOrg = useMemo(() => {
    const m = new Map();
    for (const l of locations) {
      if (!m.has(l.org_id)) m.set(l.org_id, []);
      m.get(l.org_id).push(l);
    }
    return m;
  }, [locations]);

  const term = search.trim().toLowerCase();
  const visible = useMemo(() => orgs.filter(o => {
    if (!typeFilter.has(o.type)) return false;
    if (!term) return true;
    const hay = [o.name, o.city, o.state, ...(locsByOrg.get(o.id) || []).flatMap(l => [l.name, l.city, l.state, l.address_line1])];
    return hay.some(v => v && String(v).toLowerCase().includes(term));
  }), [orgs, typeFilter, term, locsByOrg]);

  // Facilities matching the search, for the results dropdown
  const matches = useMemo(() => {
    if (!term) return [];
    const out = [];
    for (const o of orgs) {
      if (!typeFilter.has(o.type)) continue;
      const hitOrg = [o.name, o.city, o.state].some(v => v && String(v).toLowerCase().includes(term));
      if (hitOrg && o.lat != null && o.lng != null) {
        out.push({ key: o.id, label: o.name, sub: [o.city, o.state].filter(Boolean).join(', '), lat: o.lat, lng: o.lng, isOrg: true });
      }
      for (const l of (locsByOrg.get(o.id) || [])) {
        if (![l.name, l.city, l.state, l.address_line1].some(v => v && String(v).toLowerCase().includes(term))) continue;
        if (l.lat == null || l.lng == null) continue;
        out.push({ key: `${o.id}:${l.id}`, label: l.name, sub: `${[l.city, l.state].filter(Boolean).join(', ')} · part of ${o.name}`, lat: l.lat, lng: l.lng, isOrg: false });
      }
    }
    return out.slice(0, 8);
  }, [orgs, typeFilter, term, locsByOrg]);
  // Pin count mirrors the map: every geocoded location, org pin as fallback
  const pinned = useMemo(() => {
    const locCount = new Map();
    for (const l of locations) locCount.set(l.org_id, (locCount.get(l.org_id) || 0) + 1);
    // org pin + every location pin (see AdminOrgMap)
    return visible.reduce((n, o) => n + (o.lat != null && o.lng != null ? 1 : 0) + (locCount.get(o.id) || 0), 0);
  }, [visible, locations]);

  const toggleType = (val) => {
    setTypeFilter(prev => {
      const next = new Set(prev);
      if (next.has(val)) next.delete(val); else next.add(val);
      if (next.size === 0) next.add(val); // never empty
      return next;
    });
  };

  const chip = (val, label, color) => (
    <button
      onClick={() => toggleType(val)}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold border transition-all ${
        typeFilter.has(val)
          ? 'text-primary-c bg-elevated border-strong'
          : 'text-muted-c bg-transparent border-theme opacity-60'
      }`}
    >
      <span className="w-2 h-2 rounded-full" style={{ background: color }} />
      {label}
    </button>
  );

  if (loading) return <div className="flex-1 bg-primary"><PageLoader variant="block" sub="Admin" label="Loading map…" /></div>;
  if (error)   return <div className="flex-1 flex items-center justify-center bg-primary"><p className="text-danger text-[13px]">{error}</p></div>;

  return (
    <div className="flex-1 bg-primary">
      <div className="px-4 md:px-7 py-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-[22px] font-bold text-primary-c leading-tight">Organization map</h1>
          <p className="text-[12px] text-muted-c">
            {pinned} pin{pinned === 1 ? '' : 's'} across {visible.length} organization{visible.length === 1 ? '' : 's'}
            <span className="inline-flex items-center gap-1.5 ml-2">
              <span className="w-2.5 h-2.5 rounded-full bg-muted-c inline-block" style={{ background: '#6b7280' }} /> organization
              <span className="w-2 h-2 rounded-full inline-block ml-1.5" style={{ background: 'white', border: '2px solid #6b7280' }} /> location
              <span className="inline-flex items-center justify-center ml-1.5 px-1 rounded-full text-[9px] font-bold text-white" style={{ background: '#6b7280', minWidth: 14, height: 14 }}>2</span> same address
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Search organizations and locations */}
          <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-c pointer-events-none" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search locations or organizations…"
              className="w-[250px] pl-8 pr-8 py-1.5 bg-elevated border border-strong rounded-full text-[12px] text-primary-c focus:outline-none focus:border-[#0DA7CA] transition-all"
            />
            {search && (
              <button
                onClick={() => { setSearch(''); setFocus(null); }}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-c hover:text-primary-c"
              >
                <X size={13} />
              </button>
            )}
            {matches.length > 0 && (
              <div className="absolute z-20 mt-1.5 w-[300px] bg-elevated border border-theme rounded-xl shadow-2xl overflow-hidden">
                {matches.map(m => (
                  <button
                    key={m.key}
                    onClick={() => setFocus({ lat: m.lat, lng: m.lng })}
                    className="w-full text-left px-3.5 py-2 hover:bg-hover-surface transition-all border-b border-theme last:border-b-0"
                  >
                    <div className="flex items-center gap-2">
                      <MapPin size={11} className={m.isOrg ? 'text-accent-light' : 'text-muted-c'} />
                      <span className="text-[12px] font-semibold text-primary-c truncate">{m.label}</span>
                    </div>
                    <div className="text-[10.5px] text-muted-c truncate pl-[19px]">{m.sub}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
          {chip('medical_practice', 'Medical', typeColor('medical_practice'))}
          {chip('law_firm', 'Law firms', typeColor('law_firm'))}
        </div>
      </div>
      <div className="px-4 md:px-7 pb-5">
        {/* AdminShell is min-h-screen (unbounded), so flex-1 can't provide a
            height here — size the map off the viewport instead:
            60px header + ~76px page head + 20px bottom padding. */}
        <div className="rounded-xl overflow-hidden border border-theme" style={{ height: 'calc(100vh - 156px)', minHeight: 420 }}>
          <AdminOrgMap orgs={visible} locations={locations} typeColor={typeColor} focus={focus} />
        </div>
      </div>
    </div>
  );
}
